import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureReactException, logExceptionOnce } from '@hungryroot/o11y';
import { UnknownAppCrash } from '../UnknownAppCrash/UnknownAppCrash';

interface Props {
  children: ReactNode;

  /**
   * Custom fallback UI to display when an error occurs. If not provided,
   * a default error message UI will be shown.
   */
  fallback?: ReactNode;

  /**
   * Custom error key to send with error logs.
   */
  errorKey?: string;
}

interface State {
  hasError: boolean;
  errorMessage?: string;
}

/**
 * A reusable error boundary component designed to catch errors. Automatically sends
 * error logs to services such as DataDog.
 *
 * This component contains a default UI to show to the user when there is a crash.
 * To replace this default UI, pass a `fallback` prop.
 *
 * @example
 * // Basic usage
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 *
 * @example
 * // With custom fallback UI
 * <ErrorBoundary fallback={<div>Something went wrong</div>}>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const sentryEventId = captureReactException(error, info);

    logExceptionOnce(
      error,
      {
        key: this.props.errorKey ?? 'error.boundary.unhandled.crash',
        errorMessage: this.state.errorMessage,
      },
      {
        sentryEventId,
      }
    );
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      } else {
        return <UnknownAppCrash errorMessage={this.state.errorMessage} />;
      }
    }

    return this.props.children;
  }
}
